import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { CheckboxOption, Checkbox as CheckboxProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import { HTMLRenderer, body2BaseStyle } from '@ui-kit/components/HTMLRenderer'
import InlineNotification from '@ui-kit/components/InlineNotification'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { body1, body2 } from '@ui-kit/core/typography'
import { bgColor } from '@ui-kit/styles/colors'
import { theme } from '@ui-kit/theme'

import { CheckboxField } from './CheckboxField'

const checkboxStyles = tv({
  slots: {
    container: 'flex w-full flex-col gap-y-5',
    block: 'rounded-xl p-5',
    blockTitle: [body1({ bold: true }), 'max-w-[85%]'],
    checkboxContainer: 'flex flex-row items-center justify-between',
    chevronPressable: 'flex flex-1 flex-row items-end justify-end active:opacity-70',
    chevronContainer: 'web:transition-all web:duration-300',
    description: [body2(), 'ml-[30px] justify-center'],
    visibleDescriptionContainer: 'flex w-full flex-col gap-y-2',
    visibleDescription: 'ml-[30px]',
  },
  variants: {
    isExpanded: {
      true: {
        chevronContainer: 'rotate-180',
      },
    },
  },
})

interface CheckboxAccordionProps extends Omit<CheckboxOption, 'title' | 'description'> {
  title: string
  isOptional?: boolean
  description?: string
}

const CheckboxAccordion = (props: CheckboxAccordionProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const { container, checkboxContainer, chevronPressable, chevronContainer, description } =
    checkboxStyles({
      isExpanded,
    })
  return (
    <View className={container()}>
      <View className={checkboxContainer()}>
        <CheckboxField
          name={props.name ?? props.title}
          testID={props.name ?? props.title}
          label={props.title}
        />
        {props.description ? (
          <Pressable className={chevronPressable()} onPress={() => setIsExpanded(!isExpanded)}>
            <View className={chevronContainer()}>
              <PhosphorIcon name="CaretUp" size={24} />
            </View>
          </Pressable>
        ) : null}
      </View>
      {isExpanded && props.description && (
        <Text className={description()}>{props.description}</Text>
      )}
    </View>
  )
}

const visibleDescriptionStyle = { ...body2BaseStyle, color: theme.colors.grey700 }

const VisibleDescription = (props: { html: string }) => {
  const [width, setWidth] = React.useState(0)
  const source = React.useMemo(() => ({ html: props.html }), [props.html])
  const { visibleDescription } = checkboxStyles()

  return (
    <View
      className={visibleDescription()}
      onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
    >
      <HTMLRenderer source={source} baseStyle={visibleDescriptionStyle} contentWidth={width} />
    </View>
  )
}

const CheckboxWithVisibleDescription = (props: CheckboxAccordionProps) => {
  const name = props.name ?? props.title
  const { visibleDescriptionContainer } = checkboxStyles()

  return (
    <View className={visibleDescriptionContainer()}>
      <CheckboxField name={name} testID={name} label={props.title} />
      {props.description ? <VisibleDescription html={props.description} /> : null}
    </View>
  )
}

const CheckboxOptionItem = (props: CheckboxAccordionProps) =>
  props.alwaysShowDescription ? (
    <CheckboxWithVisibleDescription {...props} />
  ) : (
    <CheckboxAccordion {...props} />
  )

export const Checkbox = <TProps extends CheckboxProps<any, any>>(
  props: TProps & { flowValues: FlowValues }
) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const textProcessor = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const { control } = useFormContext()
  const { fieldState } = useController({
    control,
    name: props.name || '',
  })
  const errorMessage = fieldState.error?.root?.message

  const translatedOptions = props.fields.map((option): CheckboxAccordionProps => {
    return {
      ...option,
      title: textProcessor(option.title),
      description: option.description ? textProcessor(option.description) : undefined,
    }
  })

  const { container, block, blockTitle } = checkboxStyles()
  const background =
    props.bgColor && !props.noBackground ? [block(), bgColor({ color: props.bgColor })] : undefined

  return (
    <View className={container({ className: background })}>
      {props.blockTitle ? (
        <Text className={blockTitle()}>{textProcessor(props.blockTitle)}</Text>
      ) : null}
      {translatedOptions.map((translatedOption) => (
        <CheckboxOptionItem {...translatedOption} key={translatedOption.title} />
      ))}
      {errorMessage ? (
        <View>
          <InlineNotification variant="error" message={errorMessage} />
        </View>
      ) : null}
    </View>
  )
}
